import React, { useState, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { XYPlot, XAxis, YAxis, LineSeries, MarkSeries, AreaSeries, HorizontalGridLines, Hint } from 'react-vis';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import 'react-vis/dist/style.css';
import { useStyles } from './styles';

const IntervalGraph = ({ intervals }) => {
  const classes = useStyles();
  const [showSoc, setShowSoc] = useState(true);
  const [showDischarge, setShowDischarge] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const chartData = useMemo(() => {
    if (!intervals || intervals.length === 0) {
      return { socData: [], dischargePowerData: [], timeLabels: [] };
    }

    const socData = [];
    const dischargePowerData = [];
    const timeLabels = [];

    intervals.forEach((interval, index) => {
      const timestamp = interval.readingTimestamp || '';
      const timePart = timestamp.split(' ')[1] || '';
      const timeLabel = timePart.slice(0, 5); // HH:MM format

      const soc = interval.soc?.percent || 0;
      const dischargePower = interval.dischargePower || 0;

      socData.push({ x: index, y: soc });
      dischargePowerData.push({ x: index, y: dischargePower });
      timeLabels.push(timeLabel);
    });

    return { socData, dischargePowerData, timeLabels };
  }, [intervals]);

  const { yDomainSoc, yDomainDischarge, normalizedDischargeData } = useMemo(() => {
    if (!intervals || intervals.length === 0) {
      return { yDomainSoc: [0, 100], yDomainDischarge: [0, 100], normalizedDischargeData: [] };
    }

    const socValues = intervals.map(data => data.soc?.percent || 0);
    const dischargeValues = intervals.map(data => data.dischargePower || 0);

    const minSoc = Math.min(...socValues);
    const maxSoc = Math.max(...socValues);
    const minDischarge = Math.min(...dischargeValues);
    const maxDischarge = Math.max(...dischargeValues);

    // Calculate better Y-axis domains with some padding
    const socPadding = (maxSoc - minSoc) * 0.1 || 5;
    const dischargePadding = (maxDischarge - minDischarge) * 0.1 || 10;

    const socDomain = [Math.max(0, minSoc - socPadding), Math.min(100, maxSoc + socPadding)];
    const dischargeDomain = [Math.max(0, minDischarge - dischargePadding), maxDischarge + dischargePadding];

    // Normalize discharge data to SOC scale for proper dual-axis rendering
    const normalizedDischarge = intervals.map((interval, index) => {
      const rawValue = dischargeValues[index];
      const normalized = socDomain[0] + ((rawValue - dischargeDomain[0]) / (dischargeDomain[1] - dischargeDomain[0])) * (socDomain[1] - socDomain[0]);
      return { x: index, y: normalized, rawValue };
    });

    return {
      yDomainSoc: socDomain,
      yDomainDischarge: dischargeDomain,
      normalizedDischargeData: normalizedDischarge,
    };
  }, [intervals]);

  const tickValues = useMemo(() => {
    const ticks = [];
    const step = Math.max(1, Math.floor(chartData.timeLabels.length / 16));
    for (let i = 0; i < chartData.timeLabels.length; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [chartData.timeLabels]);

  // Pre-calculate discharge Y-axis ticks (right side)
  const dischargeTicks = useMemo(() => {
    if (!intervals || intervals.length === 0) return { positions: [], labels: {} };

    const tickCount = 5;
    const step = (yDomainDischarge[1] - yDomainDischarge[0]) / (tickCount - 1);
    const positions = [];
    const labels = {};

    for (let i = 0; i < tickCount; i++) {
      const dischargeValue = yDomainDischarge[0] + (step * i);
      const normalizedPosition = yDomainSoc[0] + 
        ((dischargeValue - yDomainDischarge[0]) / (yDomainDischarge[1] - yDomainDischarge[0])) * 
        (yDomainSoc[1] - yDomainSoc[0]);
      
      positions.push(normalizedPosition);
      labels[normalizedPosition] = Math.round(dischargeValue);
    }

    return { positions, labels };
  }, [intervals, yDomainSoc, yDomainDischarge]);

  if (!intervals || intervals.length === 0) {
    return null;
  }

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.chartTitles}>SOC (%) & Discharge Power (W) over Time</Box>
      
      <Box className={classes.legendContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showSoc}
              onChange={(e) => setShowSoc(e.target.checked)}
              style={{ color: '#7c3aed', padding: '4px' }}
              size="small"
            />
          }
          label={
            <Box className={classes.legendItem}>
              <Box component="span">SOC (%)</Box>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showDischarge}
              onChange={(e) => setShowDischarge(e.target.checked)}
              style={{ color: '#ea580c', padding: '4px' }}
              size="small"
            />
          }
          label={
            <Box className={classes.legendItem}>
              <Box component="span">Discharge Power (W)</Box>
            </Box>
          }
        />
      </Box>

      <XYPlot
        width={680}
        height={160}
        margin={{ left: 40, right: 50, top: 10, bottom: 50 }}
        yDomain={yDomainSoc}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        <HorizontalGridLines style={{ stroke: '#f1f5f9' }} />

        <XAxis
          tickValues={tickValues}
          tickFormat={(v) => chartData.timeLabels[v] || ''}
          tickLabelAngle={-45}
          style={{
            text: { fontSize: 9, fill: '#6b7280' },
          }}
        />

        <YAxis
          orientation="left"
          style={{
            text: { fontSize: 9, fill: '#7c3aed' },
          }}
        />

        <YAxis
          orientation="right"
          tickValues={dischargeTicks.positions}
          tickFormat={(v) => dischargeTicks.labels[v] || ''}
          style={{
            text: { fontSize: 9, fill: '#ea580c' },
          }}
        />

        {showSoc && (
          <AreaSeries
            data={chartData.socData}
            color="rgba(124,58,237,0.15)"
            stroke="transparent"
            curve="curveMonotoneX"
          />
        )}
        {showSoc && (
          <LineSeries
            data={chartData.socData}
            color="#7c3aed"
            strokeWidth={2}
            curve="curveMonotoneX"
            onNearestX={(value, { index }) => {
              setHoveredPoint({
                x: value.x,
                y: value.y,
                index,
                type: 'soc',
              });
            }}
          />
        )}
        {showSoc && (
          <MarkSeries
            data={chartData.socData}
            color="#7c3aed"
            size={2}
            onNearestX={(value, { index }) => {
              setHoveredPoint({
                x: value.x,
                y: value.y,
                index,
                type: 'soc',
              });
            }}
          />
        )}

        {showDischarge && (
          <AreaSeries
            data={normalizedDischargeData}
            color="rgba(234,88,12,0.15)"
            stroke="transparent"
            curve="curveMonotoneX"
          />
        )}
        {showDischarge && (
          <LineSeries
            data={normalizedDischargeData}
            color="#ea580c"
            strokeWidth={2}
            curve="curveMonotoneX"
            onNearestX={(value, { index }) => {
              setHoveredPoint({
                x: value.x,
                y: value.y,
                index,
                type: 'discharge',
              });
            }}
          />
        )}
        {showDischarge && (
          <MarkSeries
            data={normalizedDischargeData}
            color="#ea580c"
            size={2}
            onNearestX={(value, { index }) => {
              setHoveredPoint({
                x: value.x,
                y: value.y,
                index,
                type: 'discharge',
              });
            }}
          />
        )}

        {hoveredPoint && (
          <Hint value={{ x: hoveredPoint.x, y: hoveredPoint.y }}>
            <Box className={classes.hintWrapper}>
              <Box className={classes.hintTime}>
                {chartData.timeLabels[hoveredPoint.index]}
              </Box>
              <Box className={classes.hintSocData}>
                ● SOC: {chartData.socData[hoveredPoint.index]?.y.toFixed(1)}%
              </Box>
              <Box className={classes.hintDischargeData}>
                ● Discharge: {chartData.dischargePowerData[hoveredPoint.index]?.y.toFixed(1)} W
              </Box>
            </Box>
          </Hint>
        )}
      </XYPlot>
    </Box>
  );
};

export default IntervalGraph;